import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/apiClient";
import FlowDiagram from "@/components/FlowDiagram";
import Chromatogram from "@/components/Chromatogram";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Play, Square, Plus, Trash2, Loader2, FlaskConical, CheckCircle2, AlertTriangle, XCircle, Target, Download } from "lucide-react";
import { toast } from "sonner";
import { exportRunPdf } from "@/lib/pdfReport";

const DEFAULT_CFG = {
  column_id: "col-c18",
  organic_solvent_id: "sol-acn",
  elution_mode: "isocratic",
  percent_organic: 40,
  gradient: { start_percent: 10, end_percent: 90, duration_min: 10 },
  buffer_ph: 3.0,
  flow_rate_ml_min: 1.0,
  temperature_c: 30,
  wavelength_nm: 254,
  runtime_min: 12,
  injection_volume_ul: 10,
  noise_level: 0.1,
  disturbances: { baseline_drift: false, spikes: false },
};

const PLAY_MS = 7000;

function Num({ label, value, onChange, step = 1, unit, testid, min, max }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}{unit ? ` (${unit})` : ""}</Label>
      <Input
        type="number"
        className="font-data h-9"
        value={value}
        step={step}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))}
        data-testid={testid}
      />
    </div>
  );
}

function flagColor(type) {
  return type === "valid" ? "text-emerald-600" : type === "warn" ? "text-amber-600" : "text-destructive";
}

export default function Lab() {
  const location = useLocation();
  const preset = location.state || null;

  const { data: columns = [] } = useQuery({ queryKey: ["columns"], queryFn: () => api.get("/reference/columns").then((r) => r.data) });
  const { data: analytes = [] } = useQuery({ queryKey: ["analytes"], queryFn: () => api.get("/reference/analytes").then((r) => r.data) });
  const { data: solvents = [] } = useQuery({ queryKey: ["solvents"], queryFn: () => api.get("/reference/solvents").then((r) => r.data) });

  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [samples, setSamples] = useState([]);
  const [addAnalyte, setAddAnalyte] = useState("");
  const [addPpm, setAddPpm] = useState(50);

  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [loadingRun, setLoadingRun] = useState(false);
  const runningRef = useRef(false);
  const rafRef = useRef(null);

  const scenario = preset?.scenario || null;

  // Apply scenario preset once
  useEffect(() => {
    if (preset?.preset_config) {
      const pc = preset.preset_config;
      setCfg((c) => ({ ...c, ...pc, gradient: pc.gradient || c.gradient, disturbances: pc.disturbances || c.disturbances }));
      if (pc.samples) setSamples(pc.samples);
      toast.info(`Skenario dimuat: ${preset.scenario?.title || ""}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setGrad = (k, v) => setCfg((c) => ({ ...c, gradient: { ...c.gradient, [k]: v } }));
  const setDist = (k, v) => setCfg((c) => ({ ...c, disturbances: { ...c.disturbances, [k]: v } }));

  const analyteName = (id) => analytes.find((a) => a.id === id)?.name || id;

  const addSample = () => {
    if (!addAnalyte) return;
    if (samples.find((s) => s.analyte_id === addAnalyte)) {
      toast.error("Analit sudah ditambahkan");
      return;
    }
    if (!addPpm || addPpm <= 0) {
      toast.error("Konsentrasi harus > 0");
      return;
    }
    setSamples((s) => [...s, { analyte_id: addAnalyte, concentration_ppm: addPpm }]);
    setAddAnalyte("");
  };

  const stopAnim = () => {
    runningRef.current = false;
    setRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  useEffect(() => () => stopAnim(), []);

  const animate = (runtime) => {
    const start = performance.now();
    runningRef.current = true;
    setRunning(true);
    const tick = (now) => {
      if (!runningRef.current) return;
      const frac = Math.min(1, (now - start) / PLAY_MS);
      setElapsed(frac * runtime);
      if (frac < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        runningRef.current = false;
        setRunning(false);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const run = async () => {
    if (samples.length === 0) {
      toast.error("Pilih minimal satu analit untuk disuntikkan");
      return;
    }
    stopAnim();
    setResult(null);
    setElapsed(0);
    setLoadingRun(true);
    try {
      const payload = { ...cfg, samples, scenario_id: scenario?.id || null };
      if (cfg.elution_mode !== "gradient") delete payload.gradient;
      const { data } = await api.post("/lab/run", payload);
      setResult(data);
      toast.success("Injeksi dimulai — akuisisi berjalan");
      animate(data.runtime_min);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoadingRun(false);
    }
  };

  const done = result && !running;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl flex items-center gap-2">
            <FlaskConical className="h-7 w-7 text-primary" /> Lab Virtual HPLC
          </h1>
          <p className="text-muted-foreground mt-1">Atur parameter, suntikkan sampel, dan amati kromatogram.</p>
        </div>
        {scenario && (
          <Badge className="gap-1.5 py-1.5 px-3" data-testid="active-scenario-badge">
            <Target className="h-3.5 w-3.5" /> Skenario: {scenario.title}
          </Badge>
        )}
      </div>

      <FlowDiagram active={running} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Config */}
        <Card className="lg:col-span-4 p-4 rounded-md h-fit" data-testid="config-panel">
          <h2 className="font-heading font-bold text-lg mb-3">Konfigurasi</h2>

          {/* Samples (always visible) */}
          <div className="mb-3 border border-border rounded-md p-3">
            <Label className="text-xs font-semibold">Sampel & Analit</Label>
            <div className="flex gap-2 mt-2">
              <Select value={addAnalyte} onValueChange={setAddAnalyte}>
                <SelectTrigger className="h-9 flex-1" data-testid="analyte-select"><SelectValue placeholder="Pilih analit" /></SelectTrigger>
                <SelectContent>
                  {analytes.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name} <span className="text-muted-foreground font-data text-xs">(logP {a.logP})</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="number" className="h-9 w-20 font-data" value={addPpm} onChange={(e) => setAddPpm(parseFloat(e.target.value))} data-testid="ppm-input" />
              <Button size="icon" className="h-9 w-9 flex-shrink-0" onClick={addSample} data-testid="add-analyte-button"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="mt-2 space-y-1.5">
              {samples.map((s) => (
                <div key={s.analyte_id} className="flex items-center justify-between bg-secondary rounded px-2.5 py-1.5" data-testid={`sample-${s.analyte_id}`}>
                  <span className="text-sm">{analyteName(s.analyte_id)}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-data text-xs text-muted-foreground">{s.concentration_ppm} ppm</span>
                    <button onClick={() => setSamples((x) => x.filter((i) => i.analyte_id !== s.analyte_id))} data-testid={`remove-${s.analyte_id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                    </button>
                  </div>
                </div>
              ))}
              {samples.length === 0 && <p className="text-xs text-muted-foreground py-1">Belum ada analit dipilih.</p>}
            </div>
          </div>

          <Accordion type="multiple" className="w-full">
            <AccordionItem value="column">
              <AccordionTrigger className="text-sm" data-testid="acc-column">Kolom</AccordionTrigger>
              <AccordionContent className="space-y-3 pt-1">
                <Select value={cfg.column_id} onValueChange={(v) => set("column_id", v)}>
                  <SelectTrigger className="h-9" data-testid="column-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {columns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{columns.find((c) => c.id === cfg.column_id)?.description}</p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="mobile">
              <AccordionTrigger className="text-sm" data-testid="acc-mobile">Fase Gerak</AccordionTrigger>
              <AccordionContent className="space-y-3 pt-1">
                <div className="space-y-1">
                  <Label className="text-xs">Pelarut Organik</Label>
                  <Select value={cfg.organic_solvent_id} onValueChange={(v) => set("organic_solvent_id", v)}>
                    <SelectTrigger className="h-9" data-testid="solvent-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {solvents.filter((s) => s.type === "organic").map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Mode Elusi</Label>
                  <Select value={cfg.elution_mode} onValueChange={(v) => set("elution_mode", v)}>
                    <SelectTrigger className="h-9" data-testid="mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="isocratic">Isokratik</SelectItem>
                      <SelectItem value="gradient">Gradien (linear)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {cfg.elution_mode === "isocratic" ? (
                  <Num label="% Organik" value={cfg.percent_organic} onChange={(v) => set("percent_organic", v)} unit="%" min={0} max={100} testid="percent-organic-input" />
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    <Num label="Awal" value={cfg.gradient.start_percent} onChange={(v) => setGrad("start_percent", v)} unit="%" testid="grad-start" />
                    <Num label="Akhir" value={cfg.gradient.end_percent} onChange={(v) => setGrad("end_percent", v)} unit="%" testid="grad-end" />
                    <Num label="Durasi" value={cfg.gradient.duration_min} onChange={(v) => setGrad("duration_min", v)} unit="mnt" testid="grad-dur" />
                  </div>
                )}
                <Num label="pH Buffer" value={cfg.buffer_ph} onChange={(v) => set("buffer_ph", v)} step={0.1} min={1} max={12} testid="ph-input" />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="conditions">
              <AccordionTrigger className="text-sm" data-testid="acc-conditions">Kondisi Operasi</AccordionTrigger>
              <AccordionContent className="grid grid-cols-2 gap-3 pt-1">
                <Num label="Laju Alir" value={cfg.flow_rate_ml_min} onChange={(v) => set("flow_rate_ml_min", v)} step={0.1} unit="mL/mnt" testid="flow-input" />
                <Num label="Suhu" value={cfg.temperature_c} onChange={(v) => set("temperature_c", v)} unit="°C" testid="temp-input" />
                <Num label="Panjang Gelombang" value={cfg.wavelength_nm} onChange={(v) => set("wavelength_nm", v)} unit="nm" testid="wavelength-input" />
                <Num label="Runtime" value={cfg.runtime_min} onChange={(v) => set("runtime_min", v)} unit="mnt" testid="runtime-input" />
                <Num label="Vol. Injeksi" value={cfg.injection_volume_ul} onChange={(v) => set("injection_volume_ul", v)} unit="µL" testid="inj-input" />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="noise">
              <AccordionTrigger className="text-sm" data-testid="acc-noise">Noise & Gangguan</AccordionTrigger>
              <AccordionContent className="space-y-3 pt-1">
                <Num label="Level Noise" value={cfg.noise_level} onChange={(v) => set("noise_level", v)} step={0.05} min={0} max={1} testid="noise-input" />
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Drift Baseline</Label>
                  <Switch checked={cfg.disturbances.baseline_drift} onCheckedChange={(v) => setDist("baseline_drift", v)} data-testid="drift-switch" />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Spike Acak</Label>
                  <Switch checked={cfg.disturbances.spikes} onCheckedChange={(v) => setDist("spikes", v)} data-testid="spikes-switch" />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>

        {/* Chromatogram + controls */}
        <div className="lg:col-span-8 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            {!running ? (
              <Button onClick={run} disabled={loadingRun} className="gap-2 bg-[#16A34A] hover:bg-[#15803d]" data-testid="start-button">
                {loadingRun ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Mulai Akuisisi
              </Button>
            ) : (
              <Button onClick={stopAnim} variant="destructive" className="gap-2" data-testid="stop-button">
                <Square className="h-4 w-4" /> Hentikan
              </Button>
            )}
            <div className="instrument-surface rounded-md px-4 py-2 font-data text-sm">
              <span className="text-[#71717a]">Timer:</span>{" "}
              <span className={running ? "text-[#22c55e]" : "text-[#fafafa]"} data-testid="acquisition-timer">
                {elapsed.toFixed(2)}
              </span>{" "}
              <span className="text-[#71717a]">/ {cfg.runtime_min} mnt</span>
            </div>
            {running && <Badge className="bg-[#16A34A] gap-1.5"><span className="h-2 w-2 rounded-full bg-white animate-pulse" /> AKUISISI</Badge>}
          </div>

          <Chromatogram
            trace={result?.trace || []}
            cursor={elapsed}
            runtime={cfg.runtime_min}
            peaks={result?.peaks || []}
            done={done}
            elution={{ mode: cfg.elution_mode, percent_organic: cfg.percent_organic, gradient: cfg.gradient }}
          />

          {done && <AnalysisPanel result={result} scenario={scenario} config={{ ...cfg, samples }} columns={columns} />}
        </div>
      </div>
    </div>
  );
}

function AnalysisPanel({ result, scenario, config, columns = [] }) {
  const peaks = result.peaks || [];
  const colMap = Object.fromEntries(columns.map((c) => [c.id, c.name]));
  return (
    <div className="space-y-4 fade-in-up">
      {/* Score */}
      <Card className="p-5 rounded-md" data-testid="score-card">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Skor Eksperimen Otomatis</p>
            <p className="font-heading font-extrabold text-5xl font-data" data-testid="experiment-score">{result.score}</p>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              {Object.entries({ resolution: "Resolusi", tailing: "Tailing", efficiency: "Efisiensi", parameter: "Parameter" }).map(([k, lbl]) => (
                <div key={k} className="bg-secondary rounded-md px-3 py-2 min-w-[70px]">
                  <div className="font-data font-semibold">{result.score_breakdown[k]}</div>
                  <div className="text-[10px] text-muted-foreground">{lbl}</div>
                  <div className="text-[10px] text-muted-foreground font-data">×{result.score_breakdown.weights[k]}</div>
                </div>
              ))}
            </div>
            <Button variant="outline" className="gap-2" onClick={() => exportRunPdf({ data: result, config, colMap })} data-testid="export-pdf-button">
              <Download className="h-4 w-4" /> Unduh PDF
            </Button>
          </div>
        </div>
      </Card>

      {/* Feedback */}
      <Card className="p-5 rounded-md" data-testid="feedback-card">
        <h3 className="font-heading font-bold mb-3">Umpan Balik Diagnostik</h3>
        <div className="space-y-2">
          {result.feedback.map((f, i) => {
            const Icon = f.type === "success" ? CheckCircle2 : f.type === "warning" ? AlertTriangle : XCircle;
            const color = f.type === "success" ? "text-emerald-600" : f.type === "warning" ? "text-amber-600" : "text-destructive";
            return (
              <div key={i} className="flex items-start gap-2.5 text-sm">
                <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${color}`} />
                <span>{f.text}</span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Metrics table */}
      <Card className="p-5 rounded-md overflow-x-auto" data-testid="metrics-table">
        <h3 className="font-heading font-bold mb-3">Hasil Analisis Puncak</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Analit</TableHead>
              <TableHead className="text-right">tR (mnt)</TableHead>
              <TableHead className="text-right">k'</TableHead>
              <TableHead className="text-right">N</TableHead>
              <TableHead className="text-right">Area</TableHead>
              <TableHead className="text-right">Tinggi</TableHead>
              <TableHead className="text-right">Tailing</TableHead>
              <TableHead className="text-right">Rs</TableHead>
              <TableHead className="text-right">α</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {peaks.map((p) => {
              const tailOk = p.tailing >= 0.9 && p.tailing <= 1.5;
              const rs = p.resolution_prev;
              const rsType = rs == null ? null : rs >= 1.5 ? "valid" : rs >= 1.0 ? "warn" : "err";
              return (
                <TableRow key={p.analyte_id} className="font-data text-sm" data-testid={`peak-row-${p.analyte_id}`}>
                  <TableCell className="font-medium font-sans">{p.name}</TableCell>
                  <TableCell className="text-right">{p.tR}</TableCell>
                  <TableCell className="text-right">{p.k}</TableCell>
                  <TableCell className="text-right">{p.N}</TableCell>
                  <TableCell className="text-right">{p.area}</TableCell>
                  <TableCell className="text-right">{p.height}</TableCell>
                  <TableCell className={`text-right ${tailOk ? "text-emerald-600" : "text-destructive"}`}>{p.tailing}</TableCell>
                  <TableCell className={`text-right ${rsType ? flagColor(rsType) : ""}`}>{rs ?? "–"}</TableCell>
                  <TableCell className="text-right">{p.selectivity_prev ?? "–"}</TableCell>
                </TableRow>
              );
            })}
            {(result.not_eluted || []).map((p) => (
              <TableRow key={p.analyte_id} className="font-data text-sm opacity-60">
                <TableCell className="font-medium font-sans">{p.name}</TableCell>
                <TableCell colSpan={8} className="text-muted-foreground">Tidak terelusi dalam runtime</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="text-xs text-muted-foreground mt-3 font-data">
          t0 = {result.t0} mnt · N kolom ≈ {result.plate_count} · Tailing valid: 0.9–1.5 · Rs baseline ≥ 1.5
        </p>
      </Card>

      {scenario?.questions?.length > 0 && (
        <Card className="p-5 rounded-md" data-testid="scenario-questions">
          <h3 className="font-heading font-bold mb-3">Pertanyaan Diskusi</h3>
          <ol className="list-decimal list-inside space-y-1.5 text-sm text-muted-foreground">
            {scenario.questions.map((q, i) => <li key={i}>{q}</li>)}
          </ol>
        </Card>
      )}
    </div>
  );
}
